import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { matchApi } from "../api/match.js";
import { playerApi } from "../api/player.js";
import { useStore } from "../store/useStore.js";
import { EnergyBar } from "../components/EnergyBar.js";
import { UnitAvatar } from "../components/UnitAvatar.js";
import { ROLE_COLOR, unitLabel, slotFromRef } from "../lib/unitName.js";
import { getOutcomeColor, getOutcomeLabel, getOutcomeEmoji } from "../lib/game.js";
export function buildFrames(unitsA, unitsB, events) {
    const nameMap = new Map();
    const refsA = new Set(unitsA.map(u => u.ref));
    for (const u of [...unitsA, ...unitsB]) {
        const label = u.nickname || unitLabel(u.unitDefId, slotFromRef(u.ref));
        nameMap.set(u.ref, refsA.has(u.ref) ? `🔵${label}` : `🔴${label}`);
    }
    const n = (ref) => nameMap.get(ref) ?? ref;
    // Current mutable state
    const hp = new Map();
    const shield = new Map();
    const alive = new Map();
    const stunned = new Map();
    const shielded = new Map();
    const maxHp = new Map();
    for (const u of [...unitsA, ...unitsB]) {
        hp.set(u.ref, u.hp);
        maxHp.set(u.ref, u.maxHp);
        shield.set(u.ref, u.shield);
        alive.set(u.ref, true);
        stunned.set(u.ref, false);
        shielded.set(u.ref, false);
    }
    const snap = (msg, color, tick) => ({
        message: msg,
        msgColor: color,
        tick,
        units: new Map([...unitsA, ...unitsB].map(u => [u.ref, {
                hp: hp.get(u.ref),
                maxHp: maxHp.get(u.ref),
                alive: alive.get(u.ref),
                shield: shield.get(u.ref) ?? 0,
                stunned: stunned.get(u.ref) ?? false,
                shielded: shielded.get(u.ref) ?? false,
            }])),
    });
    const frames = [];
    for (const ev of events) {
        switch (ev.type) {
            case "battle_start": {
                const f = snap("⚔  BATTLE START", "#7c3aed", ev.tick);
                f.eventKind = "start";
                frames.push(f);
                break;
            }
            case "damage": {
                const p = ev.payload;
                hp.set(ev.target, p.targetHpAfter);
                const actor = n(ev.actor ?? "");
                const target = n(ev.target ?? "");
                let msg;
                if (p.action === "skill") {
                    msg = p.crit
                        ? `💥 ${actor} → ${target}  ${p.skillName} CRIT −${p.hpDamage}`
                        : `✨ ${actor} → ${target}  ${p.skillName} −${p.hpDamage}`;
                }
                else {
                    msg = p.crit
                        ? `💥 ${actor} → ${target}  CRIT −${p.hpDamage}`
                        : `⚔  ${actor} → ${target}  −${p.hpDamage}`;
                }
                if (p.shieldDamage > 0)
                    msg += ` (${p.shieldDamage} blocked)`;
                const dmgFrame = snap(msg, p.crit ? "#f59e0b" : "#ef4444", ev.tick);
                dmgFrame.actorRef = ev.actor ?? undefined;
                dmgFrame.targetRef = ev.target ?? undefined;
                dmgFrame.isSkill = p.action === "skill";
                dmgFrame.dmgAmount = p.hpDamage;
                dmgFrame.isCrit = p.crit;
                dmgFrame.skillName = p.skillName;
                dmgFrame.eventKind = "damage";
                frames.push(dmgFrame);
                break;
            }
            case "heal": {
                const p = ev.payload;
                hp.set(ev.target, p.targetHpAfter);
                const healFrame = snap(`💚 ${n(ev.actor ?? "")} heals ${n(ev.target ?? "")} +${p.amount}`, "#22c55e", ev.tick);
                healFrame.actorRef = ev.actor ?? undefined;
                healFrame.targetRef = ev.target ?? undefined;
                healFrame.isHeal = true;
                healFrame.dmgAmount = p.amount;
                healFrame.eventKind = "heal";
                frames.push(healFrame);
                break;
            }
            case "status_applied": {
                const p = ev.payload;
                if (p.status === "stun") {
                    stunned.set(ev.target, true);
                    const stunFrame = snap(`⚡ ${n(ev.actor ?? "")} STUNNED ${n(ev.target ?? "")}`, "#8b5cf6", ev.tick);
                    stunFrame.actorRef = ev.actor ?? undefined;
                    stunFrame.targetRef = ev.target ?? undefined;
                    stunFrame.isSkill = true;
                    stunFrame.eventKind = "stun";
                    frames.push(stunFrame);
                }
                else if (p.status === "shielded") {
                    shielded.set(ev.actor, true);
                    const shieldFrame = snap(`🛡 ${n(ev.actor ?? "")} raised Shield`, "#f59e0b", ev.tick);
                    shieldFrame.actorRef = ev.actor ?? undefined;
                    shieldFrame.isSkill = true;
                    shieldFrame.eventKind = "shield";
                    frames.push(shieldFrame);
                }
                break;
            }
            case "status_expired": {
                const p = ev.payload;
                if (p.status === "stun")
                    stunned.set(ev.actor, false);
                if (p.status === "shielded") {
                    shielded.set(ev.actor, false);
                    shield.set(ev.actor, 0);
                }
                break;
            }
            case "unit_died": {
                alive.set(ev.actor, false);
                const f = snap(`💀 ${n(ev.actor ?? "")} eliminated`, "#64748b", ev.tick);
                f.actorRef = ev.actor ?? undefined;
                f.eventKind = "death";
                frames.push(f);
                break;
            }
            case "battle_end": {
                const p = ev.payload;
                const f = snap(p.winner === "side_a" ? "🏆  VICTORY!" : p.winner === "draw" ? "⚖  DRAW" : "💔  DEFEAT", getOutcomeColor(p.winner), ev.tick);
                f.eventKind = "end";
                frames.push(f);
                break;
            }
        }
    }
    return frames;
}
// ─── Main component ───────────────────────────────────────────────────────────
// Matchmaking timing constants
const SEARCH_TIMEOUT_MS = 3000; // after this → show "no opponents, launching bot"
const MIN_SEARCH_MS = 4500; // minimum total searching time for UX
// ─── Auto-scale font for variable-length names ───────────────────────────────
// Step-based fontSize for names — keeps short ones bold, long ones still readable
// without ellipsis truncation. Tuned for max 16-char nicknames.
function nameFontSize(len, base) {
    if (len <= 8)
        return base;
    if (len <= 11)
        return base - 2;
    if (len <= 14)
        return Math.max(10, base - 4);
    return Math.max(9, base - 5); // 15-16 chars
}
export function PlayScreen() {
    const { player, setPlayer, squad, brainType, manualStrategy, battlePhase, battleResult, battleFrameIdx, setBattlePhase, setBattleResult, setBattleFrameIdx, clearBattle, pendingPlay, setPendingPlay, } = useStore();
    const navigate = useNavigate();
    const [error, setError] = useState(null);
    const [searchStage, setSearchStage] = useState("searching");
    const [scoutedOpponent, setScoutedOpponent] = useState(null);
    const cancelledRef = useRef(false);
    // Patch squad nicknames onto unitsA (needed for demo mode where unitsA come from hardcoded mock)
    function applyNicknames(unitsA) {
        if (!squad)
            return unitsA;
        const byDef = Object.fromEntries(squad.units.map(u => [u.unitDefId, u.nickname]));
        return unitsA.map(u => byDef[u.unitDefId] ? { ...u, nickname: byDef[u.unitDefId] } : u);
    }
    // Rebuild frames from stored result so they survive navigation
    const frames = useRef([]);
    if (battleResult && frames.current.length === 0) {
        frames.current = buildFrames(applyNicknames(battleResult.unitsA), battleResult.unitsB, battleResult.events);
    }
    const noEnergy = (player?.energy ?? 0) < 1;
    // Auto-start from scout screen when pendingPlay is set
    useEffect(() => {
        if (pendingPlay && battlePhase === "idle") {
            handleFight();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    async function handleFight() {
        if (!player || noEnergy || battlePhase === "searching")
            return; // double-tap guard
        setError(null);
        cancelledRef.current = false;
        setSearchStage("searching");
        setBattlePhase("searching");
        // Use scout-chosen preset if available, otherwise fall back to store brainType
        const scout = pendingPlay;
        setPendingPlay(null); // consume it
        // Came from Scout → we know the opponent (human, pooled bot, or fallback bot).
        // Key on opponentName, not snapshotId — bots may have no snapshot but are still scouted.
        if (scout?.opponentName) {
            setScoutedOpponent({ name: scout.opponentName, isBot: scout.opponentIsBot ?? true });
        }
        else {
            setScoutedOpponent(null);
        }
        const playOpts = scout
            ? { brainType: "preset", manualPreset: scout.preset, snapshotId: scout.snapshotId, ruleIds: scout.ruleIds }
            : brainType === "manual"
                ? {
                    brainType,
                    manualPreset: manualStrategy.preset,
                    manualAggression: manualStrategy.aggression / 100,
                    manualTargetPriority: manualStrategy.targetPriority,
                    manualFocusFire: manualStrategy.focusFire,
                }
                : { brainType };
        // After SEARCH_TIMEOUT_MS → switch visual to bot_fallback stage
        // Skip if opponent is already locked via snapshotId (real player from scout)
        const stageTimer = setTimeout(() => {
            if (!cancelledRef.current && !playOpts.snapshotId)
                setSearchStage("bot_fallback");
        }, SEARCH_TIMEOUT_MS);
        const [res] = await Promise.allSettled([
            matchApi.play(playOpts),
            new Promise(r => setTimeout(r, MIN_SEARCH_MS)),
        ]);
        clearTimeout(stageTimer);
        if (cancelledRef.current)
            return; // user cancelled during search
        if (res.status === "rejected") {
            setError(res.reason.message);
            setBattlePhase("idle");
            return;
        }
        const match = res.value;
        match.unitsA = applyNicknames(match.unitsA);
        frames.current = buildFrames(match.unitsA, match.unitsB, match.events);
        setBattleResult(match);
        setBattleFrameIdx(0);
        setBattlePhase("battling");
    }
    function handleCancel() {
        cancelledRef.current = true;
        setBattlePhase("idle");
        setSearchStage("searching");
    }
    function handleBattleDone() {
        setBattlePhase("result");
        playerApi.me().then(setPlayer).catch(() => null);
        // Trigger TG notification now that player has seen the fight result
        if (battleResult?.matchId) {
            matchApi.ack(battleResult.matchId).catch(() => null);
        }
    }
    function handlePlayAgain() {
        frames.current = [];
        clearBattle();
        navigate("/scout");
    }
    if (battlePhase === "searching")
        return _jsx(SearchingView, { stage: searchStage, scoutedOpponent: scoutedOpponent, onCancel: handleCancel });
    if (battlePhase === "battling" && battleResult)
        return (_jsx(BattleView, { result: battleResult, frames: frames.current, startFrameIdx: battleFrameIdx, onFrameIdx: setBattleFrameIdx, onDone: handleBattleDone }));
    if (battlePhase === "result" && battleResult)
        return (_jsx(ResultView, { result: battleResult, frames: frames.current, onPlayAgain: handlePlayAgain, onUpgradeSquad: () => navigate("/squad") }));
    // idle
    return (_jsxs("div", { style: { padding: 16, display: "flex", flexDirection: "column", gap: 16 }, children: [player && (_jsxs("div", { style: { background: "#12121a", borderRadius: 12, padding: 12 }, children: [_jsx("div", { style: { color: "#64748b", fontSize: 11, marginBottom: 8 }, children: "ENERGY" }), _jsx(EnergyBar, { energy: player.energy, nextEnergyAt: player.nextEnergyAt })] })), _jsx("button", { onClick: handleFight, disabled: noEnergy, style: {
                    background: noEnergy ? "#1e293b" : "linear-gradient(135deg, #7c3aed, #6d28d9)",
                    color: noEnergy ? "#475569" : "#fff",
                    border: "none", borderRadius: 14,
                    padding: "20px 0", fontSize: 20, fontWeight: 700,
                    cursor: noEnergy ? "default" : "pointer",
                    letterSpacing: 2,
                    animation: noEnergy ? undefined : "fightPulse 2.5s ease-in-out infinite",
                }, children: noEnergy ? "No Energy" : "▶  FIGHT" }), noEnergy && (_jsx("div", { style: { color: "#64748b", fontSize: 12, textAlign: "center" }, children: "Energy refills +1 every 15 min" })), error && (_jsx("div", { style: { color: "#ef4444", fontSize: 13, textAlign: "center" }, children: error }))] }));
}
// ─── Searching phase ──────────────────────────────────────────────────────────
function SearchingView({ stage, scoutedOpponent, onCancel }) {
    const [elapsed, setElapsed] = useState(0);
    useEffect(() => {
        const id = setInterval(() => setElapsed(s => s + 1), 1000);
        return () => clearInterval(id);
    }, []);
    const isFallback = stage === "bot_fallback";
    const isKnownOpponent = scoutedOpponent !== null;
    // Known opponent (came from Scout) — show "BATTLE STARTING" instead of searching
    if (isKnownOpponent) {
        return (_jsxs("div", { style: {
                display: "flex", flexDirection: "column", alignItems: "center",
                justifyContent: "center", height: "100%", gap: 28, padding: 24,
            }, children: [_jsxs("div", { style: { position: "relative", width: 96, height: 96 }, children: [_jsx("svg", { width: "96", height: "96", viewBox: "0 0 96 96", style: { position: "absolute", inset: 0, animation: "spin 2.4s linear infinite" }, children: _jsx("circle", { cx: "48", cy: "48", r: "42", fill: "none", stroke: "#7c3aed", strokeWidth: "3", strokeDasharray: "60 204", strokeLinecap: "round" }) }), _jsx("div", { style: { position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 36, animation: "pulse 1.6s ease-in-out infinite" }, children: "\u2694" })] }), _jsxs("div", { style: { textAlign: "center", display: "flex", flexDirection: "column", gap: 8 }, children: [_jsx("div", { style: { color: "#94a3b8", fontSize: 15, fontWeight: 700, letterSpacing: 2 }, children: "BATTLE STARTING" }), _jsxs("div", { style: { display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }, children: [_jsx("span", { style: { fontSize: 16 }, children: scoutedOpponent.isBot ? "🤖" : "👤" }), _jsx("span", { style: { color: scoutedOpponent.isBot ? "#475569" : "#a78bfa", fontSize: 14, fontWeight: 600 }, children: scoutedOpponent.name })] })] }), _jsx("div", { style: { display: "flex", gap: 6 }, children: [0, 1, 2, 3].map(i => (_jsx("div", { style: { width: 6, height: 6, borderRadius: 3, background: "#7c3aed", animation: `dotBounce 1.2s ease-in-out ${i * 0.18}s infinite` } }, i))) }), elapsed >= 2 && (_jsx("button", { onClick: onCancel, style: { background: "none", border: "1px solid #1e293b", color: "#475569", borderRadius: 8, padding: "8px 24px", fontSize: 13, cursor: "pointer", marginTop: 8 }, children: "Cancel" }))] }));
    }
    // No known opponent — random matchmaking searching flow
    return (_jsxs("div", { style: {
            display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", height: "100%", gap: 28, padding: 24,
        }, children: [_jsxs("div", { style: { position: "relative", width: 96, height: 96 }, children: [_jsx("svg", { width: "96", height: "96", viewBox: "0 0 96 96", style: { position: "absolute", inset: 0, animation: isFallback ? undefined : "spin 2.4s linear infinite" }, children: _jsx("circle", { cx: "48", cy: "48", r: "42", fill: "none", stroke: isFallback ? "#f59e0b" : "#7c3aed", strokeWidth: "3", strokeDasharray: isFallback ? "264" : "60 204", strokeLinecap: "round", style: { transition: "stroke 0.4s, stroke-dasharray 0.4s" } }) }), _jsx("div", { style: {
                            position: "absolute", inset: 0,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 36,
                            animation: isFallback ? undefined : "pulse 1.6s ease-in-out infinite",
                        }, children: isFallback ? "🤖" : "⚡" })] }), _jsxs("div", { style: { textAlign: "center", display: "flex", flexDirection: "column", gap: 6 }, children: [_jsx("div", { style: {
                            color: isFallback ? "#f59e0b" : "#94a3b8",
                            fontSize: 15, fontWeight: 700, letterSpacing: 2,
                            transition: "color 0.4s",
                        }, children: isFallback ? "NO OPPONENTS FOUND" : "SEARCHING…" }), _jsx("div", { style: { color: "#475569", fontSize: 12 }, children: isFallback
                            ? "Launching bot match"
                            : `Looking for a live opponent · ${elapsed}s` })] }), !isFallback && (_jsx("div", { style: { display: "flex", gap: 6 }, children: [0, 1, 2, 3].map(i => (_jsx("div", { style: {
                        width: 6, height: 6, borderRadius: 3,
                        background: "#7c3aed",
                        animation: `dotBounce 1.2s ease-in-out ${i * 0.18}s infinite`,
                    } }, i))) })), elapsed >= 1 && (_jsx("button", { onClick: onCancel, style: {
                    background: "none", border: "1px solid #1e293b",
                    color: "#475569", borderRadius: 8,
                    padding: "8px 24px", fontSize: 13, cursor: "pointer",
                    marginTop: 8,
                }, children: "Cancel" }))] }));
}
// ─── Battle phase ─────────────────────────────────────────────────────────────
const FRAME_DELAYS = { 1: 800, 2: 300, 4: 80 };
const BASE_FRAME_DELAY = 800;
const PROJ_DURATION = BASE_FRAME_DELAY * 0.42; // projectile travel time — fixed, not speed-scaled
// Approximate grid positions in SVG viewBox "0 0 100 100"
// X: sideA cards center ≈ 22%, sideB ≈ 78%
// Y: slot 0/1/2 based on header + card heights
const SLOT_Y = [24, 52, 80];
function refToPos(ref) {
    const isSideA = ref.startsWith("p1");
    const slot = Math.max(0, parseInt(ref.slice(-1), 10) - 1); // 0,1,2
    return { x: isSideA ? 22 : 78, y: SLOT_Y[slot] ?? 52 };
}
function ProjectileOverlay({ actorRef, targetRef, isSkill, isCrit }) {
    const from = refToPos(actorRef); // { x: 0-100%, y: 0-100% }
    const to = refToPos(targetRef);
    const color = isCrit ? "#fbbf24" : isSkill ? "#a78bfa" : "#ffffff";
    const size = isCrit ? 14 : isSkill ? 11 : 8;
    const glow = isCrit
        ? `0 0 10px #fbbf24, 0 0 24px #fbbf2488`
        : isSkill
            ? `0 0 8px #a78bfa, 0 0 18px #a78bfa66`
            : `0 0 6px #fff, 0 0 14px #ffffff55`;
    const dur = `${PROJ_DURATION}ms`;
    // Unique keyframe name so each projectile gets its own animation
    const id = `proj_${actorRef}_${targetRef}`.replace(/[^a-z0-9_]/gi, "_");
    return (_jsxs(_Fragment, { children: [_jsx("style", { children: `
        @keyframes ${id}_orb {
          0%   { left:${from.x}%; top:${from.y}%; opacity:1; transform:translate(-50%,-50%) scale(1);   }
          80%  { left:${to.x}%;   top:${to.y}%;   opacity:1; transform:translate(-50%,-50%) scale(1.3); }
          100% { left:${to.x}%;   top:${to.y}%;   opacity:0; transform:translate(-50%,-50%) scale(2);   }
        }
        @keyframes ${id}_trail {
          0%   { left:${from.x}%; top:${from.y}%; opacity:0.45; transform:translate(-50%,-50%) scale(0.7); }
          80%  { left:${to.x}%;   top:${to.y}%;   opacity:0.3;  transform:translate(-50%,-50%) scale(0.5); }
          100% { left:${to.x}%;   top:${to.y}%;   opacity:0;    transform:translate(-50%,-50%) scale(0);   }
        }
      ` }), _jsx("div", { style: {
                    position: "absolute",
                    left: `${from.x}%`, top: `${from.y}%`,
                    transform: "translate(-50%,-50%)",
                    width: size * 0.6, height: size * 0.6,
                    borderRadius: "50%",
                    background: color,
                    pointerEvents: "none",
                    zIndex: 30,
                    animation: `${id}_trail ${PROJ_DURATION * 1.08}ms ease-in forwards`,
                    animationDelay: `${PROJ_DURATION * 0.04}ms`,
                } }), _jsx("div", { style: {
                    position: "absolute",
                    left: `${from.x}%`, top: `${from.y}%`,
                    transform: "translate(-50%,-50%)",
                    width: size, height: size,
                    borderRadius: "50%",
                    background: color,
                    boxShadow: glow,
                    pointerEvents: "none",
                    zIndex: 31,
                    animation: `${id}_orb ${dur} ease-in forwards`,
                } })] }));
}
function BattleView({ result, frames, startFrameIdx, onFrameIdx, onDone }) {
    const [frameIdx, setFrameIdx] = useState(startFrameIdx);
    const [logOpen, setLogOpen] = useState(false);
    const [speed, setSpeed] = useState(() => {
        const s = localStorage.getItem("battleSpeed");
        return (s === "2" ? 2 : s === "4" ? 4 : 1);
    });
    // track latest frameIdx in a ref so the unmount cleanup can read it without stale closure
    const frameIdxRef = useRef(startFrameIdx);
    // Skip initial delay if resuming mid-battle
    const [started, setStarted] = useState(startFrameIdx > 0);
    useEffect(() => {
        if (started)
            return;
        const t = setTimeout(() => setStarted(true), 600);
        return () => clearTimeout(t);
    }, [started]);
    useEffect(() => {
        if (!started)
            return;
        if (frameIdx >= frames.length - 1) {
            const t = setTimeout(onDone, speed >= 4 ? 200 : 1200);
            return () => clearTimeout(t);
        }
        const delay = FRAME_DELAYS[speed] ?? BASE_FRAME_DELAY;
        const t = setTimeout(() => {
            const next = frameIdx + 1;
            frameIdxRef.current = next;
            onFrameIdx(next);
            setFrameIdx(next);
        }, delay);
        return () => clearTimeout(t);
    }, [started, frameIdx, onDone, frames, onFrameIdx, speed]);
    function handleSkip() {
        const last = frames.length - 1;
        frameIdxRef.current = last;
        onFrameIdx(last);
        setFrameIdx(last);
        setTimeout(onDone, 400);
    }
    const frame = frames[frameIdx];
    const sideA = result.unitsA;
    const sideB = result.unitsB;
    // Count how many times each unit has been hit / used a skill up to current frame.
    // Incrementing number = new key on avatar row → CSS shake animation restarts.
    const hitCounts = {};
    const skillCounts = {};
    for (let i = 0; i <= frameIdx; i++) {
        const f = frames[i];
        if (f?.targetRef)
            hitCounts[f.targetRef] = (hitCounts[f.targetRef] ?? 0) + 1;
        if (f?.actorRef && f?.isSkill)
            skillCounts[f.actorRef] = (skillCounts[f.actorRef] ?? 0) + 1;
    }
    const progress = frames.length > 1
        ? Math.round((frameIdx / (frames.length - 1)) * 100)
        : 0;
    // Damage totals per side (cumulative up to current frame)
    const sideARefs = new Set(sideA.map(u => u.ref));
    let dmgA = 0, dmgB = 0;
    for (let i = 0; i <= frameIdx; i++) {
        const f = frames[i];
        if (!f?.actorRef || !f.dmgAmount || f.isHeal)
            continue;
        if (sideARefs.has(f.actorRef))
            dmgA += f.dmgAmount;
        else
            dmgB += f.dmgAmount;
    }
    const currentTick = frame?.tick ?? 0;
    const maxTick = result.totalTicks || 1;
    return (_jsxs("div", { style: { padding: 12, display: "flex", flexDirection: "column", gap: 10, overflowY: "auto" }, children: [_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 4 }, children: [_jsxs("div", { style: {
                            display: "flex", justifyContent: "space-between", alignItems: "center",
                            fontSize: 10, letterSpacing: 1.5, color: "#475569",
                        }, children: [_jsxs("span", { style: { fontVariantNumeric: "tabular-nums" }, children: ["TICK ", _jsx("span", { style: { color: "#cbd5e1", fontWeight: 700, fontSize: 12 }, children: currentTick }), _jsxs("span", { style: { color: "#334155" }, children: [" / ", maxTick] })] }), _jsxs("div", { style: { display: "flex", gap: 4, alignItems: "center" }, children: [[1, 2, 4].map(s => (_jsxs("button", { onClick: () => { setSpeed(s); localStorage.setItem("battleSpeed", String(s)); }, style: {
                                            background: speed === s ? "#7c3aed33" : "none",
                                            border: `1px solid ${speed === s ? "#7c3aed88" : "#1e293b"}`,
                                            borderRadius: 5,
                                            color: speed === s ? "#a78bfa" : "#334155",
                                            fontSize: 10, fontWeight: 700,
                                            padding: "2px 6px",
                                            cursor: "pointer",
                                            lineHeight: 1.4,
                                        }, children: [s, "\u00D7"] }, s))), _jsx("button", { onClick: handleSkip, style: {
                                            background: "none",
                                            border: "1px solid #1e293b",
                                            borderRadius: 5,
                                            color: "#334155",
                                            fontSize: 10, fontWeight: 700,
                                            padding: "2px 6px",
                                            cursor: "pointer",
                                            lineHeight: 1.4,
                                        }, children: "\u23ED" })] })] }), _jsx("div", { style: { background: "#1e1e2e", borderRadius: 4, height: 5, overflow: "hidden", border: "1px solid #1e1e2e" }, children: _jsx("div", { style: {
                                width: `${progress}%`, height: "100%",
                                background: "linear-gradient(90deg, #7c3aed, #22d3ee)",
                                boxShadow: "0 0 8px #7c3aed99",
                                transition: `width ${FRAME_DELAYS[speed] ?? BASE_FRAME_DELAY}ms linear`,
                            } }) })] }), _jsxs("div", { style: { position: "relative" }, children: [_jsxs("div", { style: { display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 8, alignItems: "start" }, children: [_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }, children: [_jsx("div", { style: { color: "#7c3aed", fontSize: 10, fontWeight: 700, letterSpacing: 1, textAlign: "center" }, children: "YOU" }), sideA.map(u => {
                                        const isTarget = frame?.targetRef === u.ref;
                                        const isActor = frame?.actorRef === u.ref;
                                        const hasProj = !!(frame?.actorRef && frame?.targetRef && !frame?.isHeal);
                                        return (_jsx(BattleUnitBar, { unit: u, display: frame?.units.get(u.ref), isHit: isTarget && !(frame?.isHeal), isHeal: isTarget && (frame?.isHeal ?? false), isSkilling: isActor && (frame?.isSkill ?? false), isAttacking: isActor && !(frame?.isSkill) && !(frame?.isHeal), dmgAmount: isTarget ? frame?.dmgAmount : undefined, isCrit: isTarget ? (frame?.isCrit ?? false) : false, hitDelay: isTarget && hasProj ? PROJ_DURATION * 0.8 : 0, hitCount: hitCounts[u.ref] ?? 0, skillCount: skillCounts[u.ref] ?? 0 }, u.ref));
                                    })] }), _jsx("div", { style: {
                                    color: "#334155", fontSize: 16, fontWeight: 900,
                                    display: "flex", alignItems: "center", paddingTop: 28,
                                    animation: "pulse 2s ease-in-out infinite",
                                }, children: "VS" }), _jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }, children: [_jsx("div", { style: { color: "#ef4444", fontSize: 10, fontWeight: 700, letterSpacing: 1, textAlign: "center" }, children: result.opponent?.isBot
                                            ? `BOT ${result.botLevel > 1 ? `Lv${result.botLevel}` : ""}`
                                            : (result.opponent?.firstName ?? "ENEMY") }), sideB.map(u => {
                                        const isTarget = frame?.targetRef === u.ref;
                                        const isActor = frame?.actorRef === u.ref;
                                        const hasProj = !!(frame?.actorRef && frame?.targetRef && !frame?.isHeal);
                                        return (_jsx(BattleUnitBar, { unit: u, display: frame?.units.get(u.ref), isHit: isTarget && !(frame?.isHeal), isHeal: isTarget && (frame?.isHeal ?? false), isSkilling: isActor && (frame?.isSkill ?? false), isAttacking: isActor && !(frame?.isSkill) && !(frame?.isHeal), dmgAmount: isTarget ? frame?.dmgAmount : undefined, isCrit: isTarget ? (frame?.isCrit ?? false) : false, hitDelay: isTarget && hasProj ? PROJ_DURATION * 0.8 : 0, hitCount: hitCounts[u.ref] ?? 0, skillCount: skillCounts[u.ref] ?? 0 }, u.ref));
                                    })] })] }), frame?.actorRef && frame?.targetRef && !frame?.isHeal && (_jsx(ProjectileOverlay, { actorRef: frame.actorRef, targetRef: frame.targetRef, isSkill: frame.isSkill ?? false, isCrit: frame.isCrit ?? false }, `proj-${hitCounts[frame.targetRef] ?? 0}`))] }), _jsxs("div", { style: {
                    display: "grid", gridTemplateColumns: "1fr auto 1fr",
                    alignItems: "center", gap: 10,
                    background: "#0f0f17",
                    border: "1px solid #1e1e2e",
                    borderRadius: 10,
                    padding: "8px 12px",
                }, children: [_jsx(DamageSide, { label: "YOU", value: dmgA, dominant: dmgA > dmgB, color: "#7c3aed" }), _jsx("span", { style: { color: "#334155", fontSize: 9, letterSpacing: 1.5 }, children: "DMG" }), _jsx(DamageSide, { label: result.opponent?.isBot ? "BOT" : (result.opponent?.firstName ?? "ENEMY"), value: dmgB, dominant: dmgB > dmgA, color: "#ef4444", right: true })] }), _jsx(BattleChart, { frames: frames, result: result, currentIdx: frameIdx, compact: true }), _jsx("div", { style: {
                    background: "#12121a",
                    border: frame ? `1px solid ${frame.msgColor}55` : "1px solid #1e1e2e",
                    borderRadius: 12,
                    padding: "14px 16px",
                    minHeight: 72,
                    display: "flex", alignItems: "center",
                    boxShadow: frame ? `0 0 24px ${frame.msgColor}22, inset 0 0 30px ${frame.msgColor}10` : "none",
                    animation: "actionTickerIn 280ms ease-out",
                    transition: "border-color 0.15s, box-shadow 0.3s",
                }, children: _jsx(ActionTickerContent, { frame: frame, sideA: sideA, sideB: sideB }) }, `ticker-${frameIdx}`), _jsxs("div", { style: { background: "#0f0f17", border: "1px solid #1e1e2e", borderRadius: 10, overflow: "hidden" }, children: [_jsxs("button", { onClick: () => setLogOpen(v => !v), style: {
                            width: "100%", padding: "8px 12px",
                            background: "none", border: "none",
                            color: "#64748b", fontSize: 11, letterSpacing: 1,
                            cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center",
                        }, children: [_jsxs("span", { children: ["BATTLE LOG \u00B7 ", frameIdx + 1, "/", frames.length] }), _jsx("span", { style: { fontSize: 10 }, children: logOpen ? "▲" : "▼" })] }), logOpen && (_jsx("div", { style: {
                            maxHeight: 140, overflowY: "auto",
                            padding: "0 12px 10px",
                            display: "flex", flexDirection: "column", gap: 3,
                        }, children: frames
                            .slice(Math.max(0, frameIdx - 9), frameIdx + 1)
                            .reverse()
                            .map((f, i) => (_jsx("div", { style: {
                                color: i === 0 ? f.msgColor : "#334155",
                                fontSize: 11,
                                padding: "2px 0",
                                transition: "color 0.2s",
                            }, children: f.message }, i))) }))] })] }));
}
function ActionTickerContent({ frame, sideA, sideB }) {
    if (!frame) {
        return _jsx("span", { style: { color: "#64748b", fontSize: 14 }, children: "\u2014" });
    }
    const lookup = (ref) => {
        const u = sideA.find(x => x.ref === ref) || sideB.find(x => x.ref === ref);
        if (!u)
            return null;
        return {
            name: u.nickname || unitLabel(u.unitDefId, slotFromRef(u.ref)),
            sideColor: sideA.some(x => x.ref === ref) ? "#a78bfa" : "#fb7185",
            role: u.role,
        };
    };
    const actor = frame.actorRef ? lookup(frame.actorRef) : null;
    const target = frame.targetRef ? lookup(frame.targetRef) : null;
    const kind = frame.eventKind;
    // Plain centered for start/end
    if (kind === "start" || kind === "end" || !actor) {
        return (_jsx("div", { style: { width: "100%", textAlign: "center" }, children: _jsx("span", { style: {
                    color: frame.msgColor,
                    fontSize: kind === "end" ? 26 : 20,
                    fontWeight: 900,
                    letterSpacing: kind === "end" ? 4 : 2,
                    textShadow: `0 0 14px ${frame.msgColor}99`,
                }, children: frame.message.replace(/^[^\sA-Z]+\s+/, "") }) }));
    }
    // Solo actor: shield, death
    if (!target) {
        const verb = kind === "shield" ? "RAISED SHIELD" : kind === "death" ? "ELIMINATED" : "";
        return (_jsxs("div", { style: { width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }, children: [_jsx("span", { style: { color: actor.sideColor, fontSize: nameFontSize(actor.name.length, 18), fontWeight: 800, letterSpacing: 0.5 }, children: actor.name }), _jsx("span", { style: { color: frame.msgColor, fontSize: 12, fontWeight: 700, letterSpacing: 2 }, children: verb })] }));
    }
    // Duel: actor → target with verb / damage
    const isHeal = kind === "heal";
    const isStun = kind === "stun";
    const valuePrefix = isHeal ? "+" : isStun ? "" : "−";
    const valueColor = isHeal
        ? "#22c55e"
        : isStun
            ? "#a78bfa"
            : frame.isCrit ? "#fbbf24" : "#ef4444";
    const verbLabel = isHeal
        ? "HEALS"
        : isStun
            ? "STUNS"
            : frame.isSkill
                ? (frame.skillName ?? "SKILL").toUpperCase()
                : "ATTACKS";
    const valueLabel = isStun ? "" : `${valuePrefix}${frame.dmgAmount ?? 0}`;
    // YOU is always rendered on LEFT, BOT on RIGHT — arrow direction shows who's attacking
    const fromA = sideA.some(x => x.ref === frame.actorRef);
    const leftUnit = fromA ? actor : target;
    const rightUnit = fromA ? target : actor;
    const leftIsActor = fromA;
    const rightIsActor = !fromA;
    const arrowRight = fromA; // player on left attacks → arrow points right; bot on right attacks → arrow points left
    const labelForLeft = leftIsActor ? (frame.isCrit ? `CRIT · ${verbLabel}` : verbLabel) : leftUnit.role.toUpperCase();
    const labelForRight = rightIsActor ? (frame.isCrit ? `CRIT · ${verbLabel}` : verbLabel) : rightUnit.role.toUpperCase();
    const labelColorFor = (isActor) => isActor
        ? (frame.isCrit ? "#fbbf24" : isHeal ? "#22c55e" : isStun ? "#a78bfa" : "#94a3b8")
        : "#475569";
    return (_jsxs("div", { style: { width: "100%", display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "center", gap: 8 }, children: [_jsxs("div", { style: { display: "flex", flexDirection: "column", alignItems: "flex-start", minWidth: 0 }, children: [_jsx("span", { style: {
                            color: leftUnit.sideColor,
                            fontSize: nameFontSize(leftUnit.name.length, 16),
                            fontWeight: 800, letterSpacing: 0.3,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%",
                        }, children: leftUnit.name }), _jsx("span", { style: {
                            color: labelColorFor(leftIsActor),
                            fontSize: 9, fontWeight: 700, letterSpacing: 1.5, marginTop: 2,
                        }, children: labelForLeft })] }), _jsxs("div", { style: { display: "flex", flexDirection: "column", alignItems: "center", gap: 3, minWidth: 60 }, children: [_jsx(DirectionalArrow, { rightward: arrowRight, color: valueColor }), valueLabel && (_jsx("span", { style: {
                            color: valueColor,
                            fontSize: frame.isCrit ? 22 : 19,
                            fontWeight: 900,
                            letterSpacing: 0.5,
                            textShadow: `0 0 12px ${valueColor}aa`,
                            fontVariantNumeric: "tabular-nums",
                            lineHeight: 1,
                        }, children: valueLabel }))] }), _jsxs("div", { style: { display: "flex", flexDirection: "column", alignItems: "flex-end", minWidth: 0 }, children: [_jsx("span", { style: {
                            color: rightUnit.sideColor,
                            fontSize: nameFontSize(rightUnit.name.length, 16),
                            fontWeight: 800, letterSpacing: 0.3,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "100%",
                        }, children: rightUnit.name }), _jsx("span", { style: {
                            color: labelColorFor(rightIsActor),
                            fontSize: 9, fontWeight: 700, letterSpacing: 1.5, marginTop: 2,
                        }, children: labelForRight })] })] }));
}
function DirectionalArrow({ rightward, color }) {
    // Clean shaft + filled triangle head + traveling particles
    const W = 64, H = 18, MID = 9;
    const HEAD_W = 11;
    const HEAD_HALF = 6;
    const tipX = rightward ? W - 2 : 2;
    const baseX = rightward ? tipX - HEAD_W : tipX + HEAD_W;
    const sStart = rightward ? 4 : W - 4;
    const sEnd = baseX; // shaft terminates exactly at triangle base
    const dashLen = Math.abs(sEnd - sStart);
    const animEase = "cubic-bezier(0.22, 0.9, 0.32, 1)";
    // Triangle head (filled — no stroke ends visible)
    const headPoints = `${tipX},${MID} ${baseX},${MID - HEAD_HALF} ${baseX},${MID + HEAD_HALF}`;
    const innerHeadPoints = `${rightward ? tipX - 2 : tipX + 2},${MID} ${rightward ? baseX + 1 : baseX - 1},${MID - 3} ${rightward ? baseX + 1 : baseX - 1},${MID + 3}`;
    const particleAnim = rightward ? "particleTravelR" : "particleTravelL";
    return (_jsxs("svg", { width: W, height: H, viewBox: `0 0 ${W} ${H}`, style: { display: "block", overflow: "visible" }, children: [_jsx("line", { x1: sStart, y1: MID, x2: sEnd, y2: MID, stroke: color, strokeWidth: "6", strokeLinecap: "butt", opacity: "0.18", strokeDasharray: dashLen, style: {
                    animation: `arrowSlide 380ms ${animEase} forwards`,
                    filter: `drop-shadow(0 0 4px ${color}88)`,
                } }), _jsx("line", { x1: sStart, y1: MID, x2: sEnd, y2: MID, stroke: color, strokeWidth: "2.6", strokeLinecap: "butt", strokeDasharray: dashLen, style: {
                    animation: `arrowSlide 320ms ${animEase} forwards`,
                    filter: `drop-shadow(0 0 5px ${color}aa)`,
                } }), _jsx("line", { x1: sStart, y1: MID, x2: sEnd, y2: MID, stroke: "#ffffff", strokeWidth: "0.9", strokeLinecap: "butt", opacity: "0.7", strokeDasharray: dashLen, style: { animation: `arrowSlide 320ms ${animEase} forwards` } }), _jsxs("g", { style: {
                    transformBox: "fill-box",
                    transformOrigin: "center",
                    animation: `chevPop 320ms 220ms cubic-bezier(0.34, 1.56, 0.64, 1) both`,
                }, children: [_jsx("polygon", { points: headPoints, fill: color, style: { filter: `drop-shadow(0 0 6px ${color})` } }), _jsx("polygon", { points: innerHeadPoints, fill: "#ffffff", opacity: "0.5" })] }), [0, 90, 180].map((delay, i) => (_jsx("circle", { cx: sStart, cy: MID, r: "1.4", fill: "#ffffff", style: {
                    animation: `${particleAnim} 480ms ${delay}ms ease-out both`,
                    filter: `drop-shadow(0 0 4px ${color})`,
                } }, i)))] }));
}
function DamageSide({ label, value, dominant, color, right }) {
    return (_jsxs("div", { style: {
            display: "flex", flexDirection: "column",
            alignItems: right ? "flex-end" : "flex-start",
            gap: 2,
        }, children: [_jsx("span", { style: { color: "#475569", fontSize: 9, letterSpacing: 1.2 }, children: label }), _jsx("span", { style: {
                    color: dominant ? color : "#94a3b8",
                    fontSize: 17,
                    fontWeight: 800,
                    fontVariantNumeric: "tabular-nums",
                    textShadow: dominant ? `0 0 10px ${color}66` : "none",
                    transition: "color 0.3s, text-shadow 0.3s",
                }, children: value })] }));
}
function BattleUnitBar({ unit, display, isHit, isHeal, isSkilling, isAttacking, dmgAmount, isCrit, hitDelay, hitCount, skillCount }) {
    const color = ROLE_COLOR[unit.role] ?? "#64748b";
    const name = unit.nickname || unitLabel(unit.unitDefId, slotFromRef(unit.ref));
    const hp = display?.hp ?? unit.hp;
    const maxHp = unit.maxHp;
    const alive = display?.alive ?? true;
    const stunned = display?.stunned ?? false;
    const shielded = display?.shielded ?? false;
    const hpPct = Math.max(0, (hp / maxHp) * 100);
    const hpColor = hpPct > 50 ? "#22c55e" : hpPct > 25 ? "#f59e0b" : "#ef4444";
    const cdMax = unit.skillMaxCd || 1;
    const cdLeft = Math.max(0, Math.min(cdMax, unit.skillCooldown));
    const cdReady = alive && cdLeft === 0;
    const active = isHit || isHeal || isSkilling || isAttacking;
    // bg always dark — flash overlay handles the hit colour
    const bg = alive ? "#12121a" : "#0a0a0f";
    const borderCol = isHit
        ? "#ef4444"
        : isHeal ? "#22c55e"
            : isSkilling ? color
                : isAttacking ? "#ffffff66"
                    : alive ? (shielded ? "#f59e0b" : color + "44") : "#1e293b";
    const shadow = isHit
        ? (isCrit
            ? "0 0 0 2px #fbbf24, 0 0 18px #fbbf24cc, 0 0 40px #fbbf2466"
            : "0 0 0 2px #ef4444, 0 0 18px #ef4444cc, 0 0 40px #ef444466")
        : isHeal ? "0 0 0 2px #22c55e, 0 0 16px #22c55ecc, 0 0 32px #22c55e44"
            : isSkilling ? `0 0 0 2px ${color}, 0 0 16px ${color}cc, 0 0 32px ${color}44`
                : isAttacking ? "0 0 0 1px #ffffff66, 0 0 14px #ffffff33"
                    : shielded ? "0 0 0 1px #f59e0b88, 0 0 10px #f59e0b44"
                        : "none";
    // Flash overlay colour (bright interior burst that fades out)
    const flashColor = isHit
        ? (isCrit ? "#fbbf24" : "#ef4444")
        : isHeal ? "#22c55e"
            : isAttacking ? "#a78bfa"
                : null;
    const flashAnim = isHit
        ? `hitFlash ${BASE_FRAME_DELAY * 0.7}ms ease-out forwards`
        : isHeal ? `healFlash ${BASE_FRAME_DELAY * 0.7}ms ease-out forwards`
            : isAttacking ? `attackFlash ${BASE_FRAME_DELAY * 0.55}ms ease-out forwards`
                : null;
    return (_jsxs("div", { style: {
            background: bg,
            border: `2px solid ${borderCol}`,
            borderRadius: 8,
            padding: "8px 10px",
            opacity: alive ? 1 : 0.35,
            boxShadow: shadow,
            transition: active
                ? "none"
                : "border-color 0.4s ease-out, box-shadow 0.5s ease-out, opacity 0.5s",
            position: "relative",
            animation: (isHit || isHeal)
                ? `cardPop ${BASE_FRAME_DELAY * 0.35}ms ease-out ${hitDelay}ms`
                : undefined,
        }, children: [flashColor && flashAnim && (_jsx("div", { style: {
                    position: "absolute", inset: 0, borderRadius: 6,
                    background: flashColor,
                    animation: flashAnim,
                    animationDelay: `${hitDelay}ms`,
                    opacity: 0,
                    pointerEvents: "none",
                    zIndex: 1,
                } })), stunned && (_jsx("div", { style: {
                    position: "absolute", top: 2, right: 4,
                    color: "#8b5cf6", fontSize: 9, fontWeight: 700,
                    zIndex: 2,
                }, children: "STUN" })), dmgAmount !== undefined && dmgAmount > 0 && (_jsx("div", { style: {
                    position: "absolute",
                    top: -2,
                    right: 8,
                    fontSize: isCrit ? 15 : 12,
                    fontWeight: 900,
                    color: isHeal ? "#22c55e" : isCrit ? "#fbbf24" : "#ff6b6b",
                    textShadow: isHeal
                        ? "0 0 8px #22c55e"
                        : isCrit ? "0 0 12px #fbbf24, 0 0 4px #fff" : "0 0 8px #ef4444, 0 0 3px #fff4",
                    animation: `floatDmg ${BASE_FRAME_DELAY * 0.88}ms ease-out forwards`,
                    animationDelay: `${hitDelay}ms`,
                    opacity: 0,
                    pointerEvents: "none",
                    zIndex: 20,
                    letterSpacing: isCrit ? 0.5 : 0,
                }, children: isHeal ? `+${dmgAmount}` : isCrit ? `💥 ${dmgAmount}` : `-${dmgAmount}` }, `num-${hitCount}`)), _jsxs("div", { style: {
                    display: "flex", alignItems: "center", gap: 10, marginBottom: 6,
                    animation: hitCount > 0 || skillCount > 0 ? "unitShake 0.3s ease-out" : "none",
                    position: "relative", zIndex: 2,
                }, children: [_jsx("div", { style: {
                            position: "relative",
                            padding: 2,
                            borderRadius: 8,
                            boxShadow: cdReady ? `0 0 10px ${color}88, 0 0 0 1px ${color}66` : "none",
                            animation: cdReady ? "skillReadyPulse 1.6s ease-in-out infinite" : undefined,
                        }, children: _jsx(UnitAvatar, { role: unit.role, size: 44, dead: !alive }) }), _jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 }, children: [_jsx("span", { style: {
                                    fontSize: nameFontSize(name.length, 15),
                                    color: alive ? color : "#334155",
                                    fontWeight: 700, lineHeight: 1.15,
                                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                }, children: name }), _jsx("span", { style: { fontSize: 9, color, fontWeight: 700, letterSpacing: 1.4, opacity: alive ? 0.8 : 0.35, lineHeight: 1 }, children: unit.role.toUpperCase() })] }), _jsxs("div", { style: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2, flexShrink: 0 }, children: [_jsx("span", { style: { fontSize: 12, color: alive ? "#cbd5e1" : "#334155", fontWeight: 700, fontVariantNumeric: "tabular-nums" }, children: alive ? `${hp}` : "KO" }), _jsxs("span", { style: { fontSize: 9, color: "#475569", fontVariantNumeric: "tabular-nums" }, children: ["/", maxHp] })] })] }, `row-${hitCount}-${skillCount}`), _jsxs("div", { style: { background: "#0a0a0f", borderRadius: 4, height: 8, overflow: "hidden", position: "relative", zIndex: 2, border: "1px solid #1e1e2e" }, children: [_jsx("div", { style: {
                            position: "absolute", left: 0, top: 0, bottom: 0,
                            width: `${hpPct}%`,
                            background: "#ef4444",
                            opacity: isHit ? 0.55 : 0,
                            transition: isHit ? "none" : `width ${BASE_FRAME_DELAY * 0.8}ms ease-out, opacity ${BASE_FRAME_DELAY * 1.4}ms ease-out`,
                        } }), _jsx("div", { style: {
                            position: "relative",
                            width: `${hpPct}%`, height: "100%",
                            background: shielded ? `linear-gradient(90deg, ${hpColor}, #f59e0b)` : hpColor,
                            borderRadius: 3,
                            transition: `width ${BASE_FRAME_DELAY * 0.8}ms ease-out`,
                            boxShadow: alive && hpPct > 0 ? `0 0 6px ${hpColor}66 inset` : "none",
                        } })] }), _jsxs("div", { style: {
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    marginTop: 6, gap: 8, position: "relative", zIndex: 2,
                    opacity: alive ? 1 : 0.4,
                }, children: [_jsxs("div", { style: { display: "flex", gap: 8, fontSize: 9, color: "#64748b", letterSpacing: 0.5, fontVariantNumeric: "tabular-nums" }, children: [_jsxs("span", { children: [_jsx("span", { style: { color: "#94a3b8" }, children: "ATK" }), " ", unit.attack] }), _jsxs("span", { children: [_jsx("span", { style: { color: "#94a3b8" }, children: "DEF" }), " ", unit.defense] }), _jsxs("span", { children: [_jsx("span", { style: { color: "#94a3b8" }, children: "SPD" }), " ", unit.speed] })] }), _jsx("div", { style: { display: "flex", gap: 2 }, children: Array.from({ length: cdMax }).map((_, i) => {
                            const filled = i >= cdLeft;
                            return (_jsx("div", { style: {
                                    width: 6, height: 6, borderRadius: 1.5,
                                    background: filled ? color : "#1e293b",
                                    boxShadow: filled && cdReady ? `0 0 4px ${color}` : "none",
                                    transition: "background 0.3s, box-shadow 0.3s",
                                } }, i));
                        }) })] })] }, `card-${hitCount}-${skillCount}`));
}
// ─── Battle timeline chart ────────────────────────────────────────────────────
function MarkerIcon({ type, color }) {
    const glow = { filter: `drop-shadow(0 0 4px ${color})` };
    switch (type) {
        case "crit":
            // 4-point starburst (sharp horizontal+vertical spikes)
            return (_jsx("path", { d: "M0,-5 L1.1,-1.1 L5,0 L1.1,1.1 L0,5 L-1.1,1.1 L-5,0 L-1.1,-1.1 Z", fill: color, style: glow }));
        case "skill":
            // Diamond / power gem
            return (_jsxs("g", { style: glow, children: [_jsx("path", { d: "M0,-4 L4,0 L0,4 L-4,0 Z", fill: color, opacity: "0.95" }), _jsx("path", { d: "M0,-2 L2,0 L0,2 L-2,0 Z", fill: "#ffffff", opacity: "0.55" })] }));
        case "death":
            // Bold X cross
            return (_jsxs("g", { stroke: color, strokeWidth: "1.7", strokeLinecap: "round", fill: "none", style: glow, children: [_jsx("line", { x1: "-3", y1: "-3", x2: "3", y2: "3" }), _jsx("line", { x1: "3", y1: "-3", x2: "-3", y2: "3" })] }));
        case "end":
            // 5-point star
            return (_jsx("path", { d: "M0,-5 L1.5,-1.6 L5,-1.55 L2.2,0.9 L3.1,5 L0,2.5 L-3.1,5 L-2.2,0.9 L-5,-1.55 L-1.5,-1.6 Z", fill: color, style: glow }));
    }
}
function BattleChart({ frames, result, currentIdx, compact }) {
    const [hoverIdx, setHoverIdx] = useState(null);
    const svgRef = useRef(null);
    const n = frames.length;
    if (n < 2)
        return null;
    const live = currentIdx !== undefined;
    const cap = live ? Math.max(0, Math.min(n - 1, currentIdx)) : n - 1;
    const W = 300, H = compact ? 78 : 110, CY = compact ? 36 : 52, HALF = compact ? 30 : 44;
    // Compute HP % per side at each frame
    const data = frames.map(f => {
        let hpA = 0, maxA = 0, hpB = 0, maxB = 0;
        for (const u of result.unitsA) {
            hpA += Math.max(0, f.units.get(u.ref)?.hp ?? 0);
            maxA += u.maxHp;
        }
        for (const u of result.unitsB) {
            hpB += Math.max(0, f.units.get(u.ref)?.hp ?? 0);
            maxB += u.maxHp;
        }
        return {
            pctA: maxA > 0 ? hpA / maxA : 0,
            pctB: maxB > 0 ? hpB / maxB : 0,
            kind: f.eventKind,
            isCrit: !!f.isCrit,
            isSkill: !!f.isSkill,
            msg: f.message,
        };
    });
    const x = (i) => Number(((i / (n - 1)) * W).toFixed(1));
    // In live mode: only draw line/area up to current frame; rest stays empty so the chart "grows"
    const drawData = data.slice(0, cap + 1);
    const lineA = drawData.map((d, i) => `${x(i)},${(CY - d.pctA * HALF).toFixed(1)}`).join(" ");
    const lineB = drawData.map((d, i) => `${x(i)},${(CY + d.pctB * HALF).toFixed(1)}`).join(" ");
    const polyA = drawData.length > 1 ? `${x(0)},${CY} ${lineA} ${x(cap)},${CY}` : "";
    const polyB = drawData.length > 1 ? `${x(0)},${CY} ${lineB} ${x(cap)},${CY}` : "";
    const markers = [];
    data.forEach((d, i) => {
        if (i > cap)
            return;
        if (d.kind === "death")
            markers.push({ i, type: "death", xi: x(i) });
        else if (d.kind === "end")
            markers.push({ i, type: "end", xi: x(i) });
        else if (d.isCrit)
            markers.push({ i, type: "crit", xi: x(i) });
        else if (d.isSkill)
            markers.push({ i, type: "skill", xi: x(i) });
    });
    // Live "playhead" position
    const playX = live ? x(cap) : null;
    const playA = live ? (CY - data[cap].pctA * HALF) : 0;
    const playB = live ? (CY + data[cap].pctB * HALF) : 0;
    const hitPos = (svgX) => {
        const idx = Math.round((svgX / W) * (n - 1));
        setHoverIdx(Math.max(0, Math.min(n - 1, idx)));
    };
    const toSvgX = (clientX) => {
        const rect = svgRef.current?.getBoundingClientRect();
        if (!rect)
            return 0;
        return ((clientX - rect.left) / rect.width) * W;
    };
    const hovered = hoverIdx !== null ? data[hoverIdx] : null;
    const hoverX = hoverIdx !== null ? x(hoverIdx) : null;
    return (_jsxs("div", { style: { background: "#0a0a0f", border: "1px solid #1e1e2e", borderRadius: compact ? 10 : 14, overflow: "hidden" }, children: [!compact && (_jsxs("div", { style: {
                    padding: "8px 14px 4px",
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                }, children: [_jsx("span", { style: { color: "#475569", fontSize: 9, letterSpacing: 2, fontWeight: 700 }, children: "BATTLE TIMELINE" }), _jsxs("div", { style: { display: "flex", gap: 12, fontSize: 9, letterSpacing: 1 }, children: [_jsx("span", { style: { color: "#7c3aed" }, children: "\u25AC YOU" }), _jsxs("span", { style: { color: "#ef4444" }, children: ["\u25AC ", result.opponent?.isBot ? "BOT" : (result.opponent?.firstName ?? "ENEMY")] })] })] })), _jsxs("svg", { ref: svgRef, viewBox: `0 0 ${W} ${H}`, style: { width: "100%", height: compact ? 78 : 130, display: "block", cursor: live ? "default" : "crosshair" }, preserveAspectRatio: "none", onMouseMove: e => hitPos(toSvgX(e.clientX)), onMouseLeave: () => setHoverIdx(null), onTouchMove: e => { e.preventDefault(); hitPos(toSvgX(e.touches[0].clientX)); }, onTouchEnd: () => setHoverIdx(null), children: [_jsxs("defs", { children: [_jsxs("linearGradient", { id: "tcGradA", x1: "0", y1: "1", x2: "0", y2: "0", children: [_jsx("stop", { offset: "0%", stopColor: "#7c3aed", stopOpacity: "0.65" }), _jsx("stop", { offset: "100%", stopColor: "#7c3aed", stopOpacity: "0.05" })] }), _jsxs("linearGradient", { id: "tcGradB", x1: "0", y1: "0", x2: "0", y2: "1", children: [_jsx("stop", { offset: "0%", stopColor: "#ef4444", stopOpacity: "0.65" }), _jsx("stop", { offset: "100%", stopColor: "#ef4444", stopOpacity: "0.05" })] }), _jsxs("filter", { id: "tcGlowP", x: "-30%", y: "-80%", width: "160%", height: "260%", children: [_jsx("feGaussianBlur", { stdDeviation: "2", result: "b" }), _jsxs("feMerge", { children: [_jsx("feMergeNode", { in: "b" }), _jsx("feMergeNode", { in: "SourceGraphic" })] })] }), _jsxs("filter", { id: "tcGlowR", x: "-30%", y: "-80%", width: "160%", height: "260%", children: [_jsx("feGaussianBlur", { stdDeviation: "2", result: "b" }), _jsxs("feMerge", { children: [_jsx("feMergeNode", { in: "b" }), _jsx("feMergeNode", { in: "SourceGraphic" })] })] })] }), [0.25, 0.5, 0.75].map(t => (_jsx("line", { x1: "0", y1: CY - t * HALF, x2: W, y2: CY - t * HALF, stroke: "#1e293b", strokeWidth: "0.5", strokeDasharray: "3 4" }, t))), [0.25, 0.5, 0.75].map(t => (_jsx("line", { x1: "0", y1: CY + t * HALF, x2: W, y2: CY + t * HALF, stroke: "#1e293b", strokeWidth: "0.5", strokeDasharray: "3 4" }, t))), _jsx("polygon", { points: polyA, fill: "url(#tcGradA)" }), _jsx("polygon", { points: polyB, fill: "url(#tcGradB)" }), _jsx("polyline", { points: lineA, fill: "none", stroke: "#7c3aed", strokeWidth: "1.8", filter: "url(#tcGlowP)" }), _jsx("polyline", { points: lineB, fill: "none", stroke: "#ef4444", strokeWidth: "1.8", filter: "url(#tcGlowR)" }), _jsx("line", { x1: "0", y1: CY, x2: W, y2: CY, stroke: "#334155", strokeWidth: "0.8" }), markers.map((m, mi) => {
                        const color = m.type === "death" ? "#64748b" : m.type === "end" ? "#22c55e" : m.type === "crit" ? "#fbbf24" : "#a78bfa";
                        const isNewest = live && mi === markers.length - 1 && m.i === cap;
                        return (_jsxs("g", { children: [_jsx("line", { x1: m.xi, y1: "0", x2: m.xi, y2: H, stroke: color, strokeWidth: "0.7", strokeDasharray: "2 3", opacity: "0.5" }), _jsx("g", { transform: `translate(${m.xi} ${CY})`, children: _jsx("g", { style: {
                                            transformBox: "fill-box",
                                            transformOrigin: "center",
                                            animation: isNewest ? "markerPop 480ms cubic-bezier(0.34, 1.56, 0.64, 1) both" : undefined,
                                        }, children: _jsx(MarkerIcon, { type: m.type, color: color }) }) })] }, `m-${m.i}-${m.type}`));
                    }), playX !== null && (_jsxs("g", { children: [_jsx("line", { x1: playX, y1: "0", x2: playX, y2: H, stroke: "#ffffff", strokeWidth: "0.9", opacity: "0.55" }), _jsx("circle", { cx: playX, cy: playA, r: "3.2", fill: "#a78bfa", stroke: "#fff", strokeWidth: "0.8", style: { filter: "drop-shadow(0 0 5px #a78bfa)" } }), _jsx("circle", { cx: playX, cy: playB, r: "3.2", fill: "#fb7185", stroke: "#fff", strokeWidth: "0.8", style: { filter: "drop-shadow(0 0 5px #fb7185)" } })] })), !live && hoverX !== null && (_jsxs(_Fragment, { children: [_jsx("line", { x1: hoverX, y1: "0", x2: hoverX, y2: H, stroke: "#ffffff", strokeWidth: "0.8", opacity: "0.35" }), hovered && (_jsxs(_Fragment, { children: [_jsx("circle", { cx: hoverX, cy: CY - hovered.pctA * HALF, r: "3", fill: "#7c3aed", stroke: "#fff", strokeWidth: "0.8" }), _jsx("circle", { cx: hoverX, cy: CY + hovered.pctB * HALF, r: "3", fill: "#ef4444", stroke: "#fff", strokeWidth: "0.8" })] }))] }))] }), !compact && (_jsx("div", { style: {
                    padding: "4px 14px 10px",
                    minHeight: 28,
                    display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8,
                }, children: hovered ? (_jsxs(_Fragment, { children: [_jsx("span", { style: {
                                color: "#94a3b8", fontSize: 10, letterSpacing: 0.3, lineHeight: 1.3, flex: 1,
                            }, children: hovered.msg.replace(/[🔵🔴]/g, "").trim() }), _jsxs("div", { style: { display: "flex", gap: 10, flexShrink: 0, fontSize: 10, fontVariantNumeric: "tabular-nums" }, children: [_jsxs("span", { style: { color: "#a78bfa" }, children: [Math.round(hovered.pctA * 100), "%"] }), _jsxs("span", { style: { color: "#fb7185" }, children: [Math.round(hovered.pctB * 100), "%"] })] })] })) : (_jsxs("div", { style: { display: "flex", gap: 16, fontSize: 9, color: "#334155", letterSpacing: 1 }, children: [_jsx("span", { children: "! crit" }), _jsx("span", { style: { color: "#475569" }, children: "\u00B7 skill" }), _jsx("span", { children: "\u2715 death" }), _jsx("span", { children: "\u2605 end" })] })) }))] }));
}
// ─── Result phase ─────────────────────────────────────────────────────────────
function ResultView({ result, frames, onPlayAgain, onUpgradeSquad }) {
    const [show, setShow] = useState(false);
    const [showLog, setShowLog] = useState(false);
    useEffect(() => { const t = setTimeout(() => setShow(true), 80); return () => clearTimeout(t); }, []);
    const logFrames = frames.filter(f => f.message !== "⚔  BATTLE START");
    const outcomeColor = getOutcomeColor(result.winner);
    const outcomeLabel = getOutcomeLabel(result.winner);
    const outcomeEmoji = getOutcomeEmoji(result.winner);
    const { creditsEarned, ratingDelta, rankPointsDelta } = result.rewards;
    return (_jsxs("div", { style: {
            padding: 16, display: "flex", flexDirection: "column", gap: 12,
            opacity: show ? 1 : 0,
            transform: show ? "translateY(0)" : "translateY(20px)",
            transition: "opacity 0.5s, transform 0.5s",
            overflowY: "auto",
        }, children: [_jsxs("div", { style: {
                    position: "relative", overflow: "hidden",
                    background: `${outcomeColor}12`,
                    border: `2px solid ${outcomeColor}50`,
                    borderRadius: 20,
                    padding: "28px 20px 22px",
                    textAlign: "center",
                    animation: show ? "resultIn 0.5s ease-out both" : undefined,
                }, children: [_jsx("div", { style: {
                            position: "absolute", inset: 0,
                            background: `radial-gradient(ellipse at 50% 0%, ${outcomeColor}25, transparent 65%)`,
                            pointerEvents: "none",
                        } }), _jsx("div", { style: {
                            fontSize: 52, lineHeight: 1, marginBottom: 10, position: "relative",
                            animation: show ? "outcomeGlow 3s ease-in-out infinite" : undefined,
                            color: outcomeColor,
                        }, children: outcomeEmoji }), _jsx("div", { style: {
                            color: outcomeColor,
                            fontSize: 30, fontWeight: 900, letterSpacing: 6,
                            position: "relative",
                            animation: "outcomeGlow 3s ease-in-out infinite",
                        }, children: outcomeLabel }), _jsxs("div", { style: { color: "#475569", fontSize: 11, marginTop: 8, letterSpacing: 1 }, children: [result.totalTicks, " TICKS \u00B7 ", result.endReason.replace("_", " ").toUpperCase()] })] }), _jsxs("div", { style: {
                    background: "#12121a",
                    border: "1px solid #1e1e2e",
                    borderRadius: 14,
                    display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
                    animation: show ? "neonIn 0.5s 0.15s ease-out both" : undefined,
                }, children: [_jsx(RewardChip, { label: "Credits", value: `+${creditsEarned}`, icon: "\u25C8", color: "#22d3ee", border: "right", delay: "0.2s" }), _jsx(RewardChip, { label: "Rating", value: ratingDelta >= 0 ? `+${ratingDelta}` : `${ratingDelta}`, icon: "\u25C7", color: "#f59e0b", border: "right", delay: "0.3s" }), _jsx(RewardChip, { label: "RP", value: rankPointsDelta >= 0 ? `+${rankPointsDelta}` : `${rankPointsDelta}`, icon: "\u25B2", color: "#7c3aed", delay: "0.4s" })] }), _jsxs("div", { style: {
                    background: "#12121a",
                    border: "1px solid #1e1e2e",
                    borderRadius: 14,
                    padding: "12px 16px",
                    display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 10, alignItems: "center",
                }, children: [_jsx(SurvivalSide, { label: "YOU", alive: result.sideA.unitsAlive, hpPct: result.sideA.totalHpPercent, color: "#7c3aed" }), _jsx("div", { style: { color: "#334155", fontWeight: 900, fontSize: 14, textAlign: "center" }, children: "VS" }), _jsx(SurvivalSide, { label: `${result.opponent?.isBot ? "🤖" : "👤"} ${result.opponent?.firstName ?? "BOT"}`, alive: result.sideB.unitsAlive, hpPct: result.sideB.totalHpPercent, color: "#ef4444", right: true })] }), _jsx(BattleChart, { frames: frames, result: result }), _jsxs("div", { style: { background: "#12121a", border: "1px solid #1e1e2e", borderRadius: 12, overflow: "hidden" }, children: [_jsxs("button", { onClick: () => setShowLog(v => !v), style: {
                            width: "100%", padding: "10px 14px",
                            background: "none", border: "none",
                            color: "#64748b", fontSize: 12,
                            cursor: "pointer", textAlign: "left",
                            display: "flex", justifyContent: "space-between",
                        }, children: [_jsxs("span", { children: ["Battle Log (", logFrames.length, " events)"] }), _jsx("span", { children: showLog ? "▲" : "▼" })] }), showLog && (_jsx("div", { style: {
                            maxHeight: 200, overflowY: "auto",
                            padding: "0 14px 12px",
                            display: "flex", flexDirection: "column", gap: 4,
                        }, children: logFrames.map((f, i) => (_jsxs("div", { style: { display: "flex", gap: 8, alignItems: "baseline" }, children: [_jsx("span", { style: { color: "#334155", fontSize: 10, flexShrink: 0 }, children: String(i + 1).padStart(2, "0") }), _jsx("span", { style: { color: f.msgColor, fontSize: 11 }, children: f.message })] }, i))) }))] }), _jsx("button", { onClick: onPlayAgain, style: {
                    background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
                    color: "#fff", border: "none", borderRadius: 14,
                    padding: "16px 0", fontSize: 17, fontWeight: 700,
                    cursor: "pointer", letterSpacing: 1,
                    animation: "fightPulse 2.5s ease-in-out infinite",
                }, children: "\u25B6  FIGHT AGAIN" }), _jsxs("button", { onClick: onUpgradeSquad, style: {
                    background: "#12121a", color: "#22d3ee",
                    border: "1px solid #22d3ee33", borderRadius: 14,
                    padding: "12px 0", fontSize: 14, fontWeight: 600,
                    cursor: "pointer", letterSpacing: 0.5,
                }, children: ["\u25C8 ", result.rewards.creditsEarned > 0 ? `+${result.rewards.creditsEarned} credits — upgrade squad →` : "upgrade squad →"] })] }));
}
function RewardChip({ label, value, icon, color, border, delay = "0s" }) {
    return (_jsxs("div", { style: {
            display: "flex", flexDirection: "column", alignItems: "center",
            gap: 4, padding: "14px 0",
            borderRight: border === "right" ? "1px solid #1e1e2e" : undefined,
        }, children: [_jsx("span", { style: { color: "#475569", fontSize: 10, letterSpacing: 1 }, children: label.toUpperCase() }), _jsxs("div", { style: {
                    display: "flex", alignItems: "center", gap: 5,
                    animation: `countUp 0.4s ${delay} ease-out both`,
                }, children: [_jsx("span", { style: { color, fontSize: 14 }, children: icon }), _jsx("span", { style: {
                            color, fontWeight: 900, fontSize: 22,
                            textShadow: `0 0 12px ${color}88`,
                        }, children: value })] })] }));
}
function SurvivalSide({ label, alive, hpPct, color, right }) {
    const dots = Array.from({ length: 3 }, (_, i) => (_jsx("div", { style: {
            width: 10, height: 10, borderRadius: "50%",
            background: i < alive ? color : "#1e293b",
            border: `1px solid ${i < alive ? color : "#334155"}`,
            boxShadow: i < alive ? `0 0 6px ${color}88` : "none",
            transition: "box-shadow 0.3s",
        } }, i)));
    return (_jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 6, alignItems: right ? "flex-end" : "flex-start" }, children: [_jsx("div", { style: { color: "#475569", fontSize: 10, letterSpacing: 1 }, children: label }), _jsx("div", { style: { display: "flex", gap: 4 }, children: dots }), _jsxs("div", { style: { color, fontWeight: 700, fontSize: 13 }, children: [hpPct, "% HP"] }), _jsx("div", { style: { width: "100%", background: "#0a0a0f", borderRadius: 3, height: 5, overflow: "hidden" }, children: _jsx("div", { style: {
                        width: `${hpPct}%`, height: "100%",
                        background: color, borderRadius: 3,
                        transition: "width 0.8s ease-out",
                        float: right ? "right" : undefined,
                    } }) })] }));
}
